import { Injectable } from '@angular/core';
import { Project } from '../models/project.model';
import { DepartmentEntry } from './department-schemes.service';
import { HM_DEPARTMENT_ALIASES, resolveHmDepartmentName } from '../data/hm-department-master';

/**
 * Single source of truth for mapping Firestore projects → master departments.
 * Used by the HM dashboard rollup and the departments page merge so counts stay aligned.
 */
@Injectable({ providedIn: 'root' })
export class DepartmentMatchingService {
  /** Legacy / import abbreviations → canonical shortName or name. */
  private readonly deptAbbrToShortName: Record<string, string> = {
    ...HM_DEPARTMENT_ALIASES,
    WR: 'WR',
    RD: 'RD',
    PWD: 'Works',
    'H&UD': 'H&UD',
    Sports: 'Sports',
    Health: 'Health',
    Tourism: 'Tourism',
    HE: 'HE',
    WCD: 'W&CD',
    'W&CD': 'W&CD',
    Cultural: 'Cultural',
    Culture: 'Cultural',
    OLLC: 'Cultural',
    RWSS: 'RWSS',
    'PR&DW': 'PR Block',
    'PR Block': 'PR Block',
    PC: 'PR Block',
    SAM: 'School Mass',
    'School Mass': 'School Mass',
    'R&DM': 'R&DM',
    Works: 'Works',
    'Water Resources': 'WR',
    'Rural Division': 'RD',
    'Rural Development': 'RD',
    'High Education': 'HE',
    'Higher Education': 'HE',
    Education: 'HE',
    'Panchayati Raj (Block) & Planning & co-ordinator': 'PR Block',
    'Panchayati Raj & Drinking Water': 'PR Block',
    'Planning & Convergence': 'PR Block',
    'Housing & Urban Development': 'H&UD',
    'Health & Family Welfare': 'Health',
    'Sports & Youth Services': 'Sports',
    'Odia Language, Literature & Culture': 'Cultural',
    'Schools and Mass': 'School Mass',
    'Revenue & Disaster Management': 'R&DM',
  };

  normalizeKey(value: string | null | undefined): string {
    return (value || '')
      .trim()
      .toLowerCase()
      .replace(/[\s&]/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/_+/g, '_');
  }

  /** Resolve a raw project.dept label to a master {@link DepartmentEntry}. */
  resolveDepartment(
    proj: Pick<Project, 'dept'>,
    departments: DepartmentEntry[]
  ): DepartmentEntry | undefined {
    const raw = (proj.dept || '').trim();
    if (!raw) return undefined;

    const canonicalName = resolveHmDepartmentName(raw);
    const deptByShort = new Map(departments.map(d => [d.shortName, d]));
    const deptByKey = new Map<string, DepartmentEntry>();
    for (const d of departments) {
      deptByKey.set(d.name.toLowerCase(), d);
      deptByKey.set(d.shortName.toLowerCase(), d);
      deptByKey.set(this.normalizeKey(d.name), d);
      deptByKey.set(this.normalizeKey(d.shortName), d);
    }

    const mappedShort = this.deptAbbrToShortName[raw] ?? raw;
    return (
      deptByShort.get(mappedShort) ??
      departments.find(d => d.name === canonicalName) ??
      deptByKey.get(raw.toLowerCase()) ??
      deptByKey.get(this.normalizeKey(raw)) ??
      departments.find(
        d =>
          this.normalizeKey(d.name) === this.normalizeKey(raw) ||
          this.normalizeKey(d.shortName) === this.normalizeKey(raw) ||
          this.normalizeKey(d.name) === this.normalizeKey(canonicalName)
      )
    );
  }

  /** True when a live Firestore project belongs to the given department (same rules as dept page merge). */
  projectBelongsToDepartment(
    proj: Project,
    dept: DepartmentEntry,
    departments: DepartmentEntry[]
  ): boolean {
    if (!proj.dept?.trim()) return false;
    const resolved = this.resolveDepartment(proj, departments);
    return resolved?.name === dept.name;
  }

  /** Projects in a department that will appear on the departments table (requires scheme + resolvable dept). */
  projectsForDepartment(
    projects: Project[],
    dept: DepartmentEntry,
    departments: DepartmentEntry[]
  ): Project[] {
    return projects.filter(
      p => !!p.scheme?.trim() && this.projectBelongsToDepartment(p, dept, departments)
    );
  }

  /** Group all Firestore projects by master department (includes projects without scheme). */
  groupByDepartment(
    projects: Project[],
    departments: DepartmentEntry[]
  ): Map<string, Project[]> {
    const map = new Map<string, Project[]>();
    for (const d of departments) {
      map.set(d.name, []);
    }
    for (const p of projects) {
      const dept = this.resolveDepartment(p, departments);
      if (!dept) continue;
      map.get(dept.name)!.push(p);
    }
    return map;
  }
}
